import { useState } from "react"
import { API_ORIGIN } from "@/constants"
import { OnboardingStep } from "@/hooks/use-onboarding"
import { OnboardingStepActions } from '@/components/onboarding/onboarding-step-actions'
import { FormField } from "@/components/ui/form-field"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { OnboardingStepNext, runOnboardingStepNext } from "@/lib/onboarding-step-nav"

interface BasicInfoStepProps {
  step: OnboardingStep
  onNext: OnboardingStepNext
  onBack?: () => void
  isCompleting: boolean
  canGoBack: boolean
}

export function BasicInfoStep({ onNext, onBack, isCompleting, canGoBack }: BasicInfoStepProps) {
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [bio, setBio] = useState("")
  const [city, setCity] = useState("")
  const [country, setCountry] = useState("")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async () => {
    if (!firstName || !lastName) {
      setError('First name and last name are required')
      return
    }

    setSaving(true)
    setError(null)

    try {
      const token = localStorage.getItem('auth_token')
      const response = await fetch(`${API_ORIGIN}/api/profiles/onboarding/basic-info`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          bio: bio || null,
          city: city || null,
          country: country || null,
        }),
      })

      if (response.ok) {
        await runOnboardingStepNext(onNext, setError)
      } else {
        const errorData = await response.json().catch(() => ({}))
        setError(errorData.error || 'Failed to save information')
      }
    } catch (err) {
      console.error('Error saving basic info:', err)
      setError('Network error. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="First Name *" htmlFor="first-name">
            <Input
              id="first-name"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={saving || isCompleting}
              required
            />
          </FormField>
          <FormField label="Last Name *" htmlFor="last-name">
            <Input
              id="last-name"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={saving || isCompleting}
              required
            />
          </FormField>
        </div>

        <FormField label="Bio" htmlFor="bio">
          <Textarea
            id="bio"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            disabled={saving || isCompleting}
            rows={4}
            placeholder="Tell us a bit about yourself..."
          />
        </FormField>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="City" htmlFor="city">
            <Input
              id="city"
              autoComplete="address-level2"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              disabled={saving || isCompleting}
            />
          </FormField>
          <FormField label="Country" htmlFor="country">
            <Input
              id="country"
              autoComplete="country-name"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              disabled={saving || isCompleting}
            />
          </FormField>
        </div>

        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}
      </div>

      <OnboardingStepActions
        canGoBack={canGoBack}
        onBack={onBack}
        onContinue={handleSave}
        backDisabled={saving || isCompleting}
        continueDisabled={saving || isCompleting || !firstName || !lastName}
        isBusy={saving}
        continueBusyLabel="Saving..."
      />
    </div>
  )
}
